/**
 * ReceiptInfoSection Component
 *
 * Form section for supplier, date, invoice reference, BL number, file uploads,
 * and definitive/provisoire toggle.
 *
 * @see US-36.7 - Upload BL fournisseur
 */

import { X } from 'lucide-react';
import { Select, Input, FileUpload } from '@/components/ui';
import { DatePicker, type DateRange } from '@/components/ui';
import { useBackdatingLimit } from '@/hooks/useBackdatingLimit';

interface ReceiptInfoSectionProps {
  isFromOrder: boolean;
  supplierId: string;
  receiptDateRange: DateRange;
  supplierInvoiceRef: string;
  blNumber: string;
  isDefinitive: boolean;
  supplierOptions: { value: string; label: string }[];
  suppliersLoading: boolean;
  onSupplierChange: (value: string) => void;
  onDateChange: (range: DateRange) => void;
  onInvoiceRefChange: (value: string) => void;
  onBlNumberChange: (value: string) => void;
  onIsDefinitiveChange: (value: boolean) => void;
  onBlFileSelect?: (file: File) => void;
  onInvoiceFileSelect?: (file: File) => void;
  pendingBlFiles?: File[];
  pendingInvoiceFiles?: File[];
  onRemovePendingBlFile?: (index: number) => void;
  onRemovePendingInvoiceFile?: (index: number) => void;
  isViewMode?: boolean;
}

function PendingFilesList({
  files,
  onRemove,
}: {
  files: File[];
  onRemove?: (index: number) => void;
}) {
  if (files.length === 0) return null;

  return (
    <ul className="mt-2 space-y-1">
      {files.map((file, i) => (
        <li key={`${file.name}-${i}`} className="flex items-center gap-2 text-sm text-neutral-600 bg-white rounded px-2 py-1 border border-neutral-200">
          <span className="truncate flex-1">{file.name}</span>
          <span className="text-xs text-neutral-400 shrink-0">
            {(file.size / 1024).toFixed(0)} KB
          </span>
          {onRemove && (
            <button
              type="button"
              onClick={() => onRemove(i)}
              className="p-0.5 text-neutral-400 hover:text-danger-500 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}

export function ReceiptInfoSection({
  isFromOrder,
  supplierId,
  receiptDateRange,
  supplierInvoiceRef,
  blNumber,
  isDefinitive,
  supplierOptions,
  suppliersLoading,
  onSupplierChange,
  onDateChange,
  onInvoiceRefChange,
  onBlNumberChange,
  onIsDefinitiveChange,
  onBlFileSelect,
  onInvoiceFileSelect,
  pendingBlFiles = [],
  pendingInvoiceFiles = [],
  onRemovePendingBlFile,
  onRemovePendingInvoiceFile,
  isViewMode = false,
}: ReceiptInfoSectionProps) {
  const { minDate } = useBackdatingLimit();

  return (
    <div className="bg-neutral-50 rounded-lg border border-neutral-200 p-6">
      <h3 className="text-lg font-semibold text-dark-500 mb-4">Informations</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Supplier (only for free receipt) */}
        {!isFromOrder && (
          <Select
            label="Fournisseur *"
            value={supplierId}
            onChange={onSupplierChange}
            options={supplierOptions}
            placeholder="Selectionner un fournisseur"
            disabled={suppliersLoading || isViewMode}
          />
        )}

        {/* Receipt date */}
        <div>
          <label className="block text-sm font-medium text-dark-500 mb-1.5">
            Date de reception *
          </label>
          <DatePicker
            asSingle
            showTime
            minDate={minDate}
            maxDate={new Date()}
            value={receiptDateRange}
            onChange={onDateChange}
            placeholder="Selectionner une date"
            disabled={isViewMode}
          />
        </div>

        {/* Invoice reference */}
        <Input
          label="Reference facture fournisseur"
          value={supplierInvoiceRef}
          onChange={(e) => onInvoiceRefChange(e.target.value)}
          placeholder="Ex: FAC-2024-001"
          disabled={isViewMode}
        />

        {/* BL Number */}
        <Input
          label="N° Bon de livraison"
          value={blNumber}
          onChange={(e) => onBlNumberChange(e.target.value)}
          placeholder="Ex: BL-2026-001"
          disabled={isViewMode}
        />
      </div>

      {/* File uploads — BL + Facture */}
      {!isViewMode && (onBlFileSelect || onInvoiceFileSelect) && (
        <div className="mt-4 pt-4 border-t border-neutral-200">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {onBlFileSelect && (
              <div>
                <FileUpload
                  label="BL fournisseur"
                  accept=".pdf,.jpg,.jpeg,.png"
                  maxSizeMB={10}
                  onFileSelect={onBlFileSelect}
                  disabled={isViewMode}
                />
                <PendingFilesList files={pendingBlFiles} onRemove={onRemovePendingBlFile} />
              </div>
            )}
            {onInvoiceFileSelect && (
              <div>
                <FileUpload
                  label="Facture fournisseur"
                  accept=".pdf,.jpg,.jpeg,.png"
                  maxSizeMB={10}
                  onFileSelect={onInvoiceFileSelect}
                  disabled={isViewMode}
                />
                <PendingFilesList files={pendingInvoiceFiles} onRemove={onRemovePendingInvoiceFile} />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Definitive / Provisoire radio group (only when linked to a PO) */}
      {isFromOrder && (
        <div className="mt-4 pt-4 border-t border-neutral-200">
          <label className="block text-sm font-medium text-dark-500 mb-2">
            Type de reception
          </label>
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="isDefinitive"
                checked={isDefinitive}
                onChange={() => onIsDefinitiveChange(true)}
                disabled={isViewMode}
                className="w-4 h-4 text-primary-600 border-neutral-300 focus:ring-primary-500"
              />
              <span className="text-sm text-dark-500">
                Definitive
              </span>
              <span className="text-xs text-neutral-500">
                (cloture la commande)
              </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="isDefinitive"
                checked={!isDefinitive}
                onChange={() => onIsDefinitiveChange(false)}
                disabled={isViewMode}
                className="w-4 h-4 text-primary-600 border-neutral-300 focus:ring-primary-500"
              />
              <span className="text-sm text-dark-500">
                Provisoire
              </span>
              <span className="text-xs text-neutral-500">
                (attend d'autres livraisons)
              </span>
            </label>
          </div>
        </div>
      )}
    </div>
  );
}
